"use client"
import { Info } from "lucide-react"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"

interface ImageGenerationAspectSelectorProps {
  imageUrl: string
  landscape: boolean | null
  onLandscapeChange: (landscape: boolean) => void
  squareCredits: number
  landscapeCredits: number
  title?: string
  subtitle?: string
}

export function ImageGenerationAspectSelector({
  imageUrl,
  landscape,
  onLandscapeChange,
  squareCredits,
  landscapeCredits,
  title = "Canvas Orientation",
  subtitle = "Choose how your canvas will be generated",
}: ImageGenerationAspectSelectorProps) {
  console.log(
    "[v0] ImageGenerationAspectSelector - landscape:",
    landscape,
    "squareCredits:",
    squareCredits,
    "landscapeCredits:",
    landscapeCredits,
  )

  return (
    <div className="space-y-3">
      <div>
        <h3 className="text-lg font-semibold">{title}</h3>
        <p className="text-sm text-gray-500">{subtitle}</p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        {/* Square Option */}
        <button
          type="button"
          onClick={() => onLandscapeChange(false)}
          className={`relative p-3 rounded-lg border-2 transition-all ${
            landscape === false ? "border-[#40A1AB] bg-[#40A1AB]/10" : "border-gray-200 hover:border-gray-300"
          }`}
        >
          <div className="flex items-start justify-between mb-2">
            <div className="flex items-center gap-2">
              <input
                type="radio"
                checked={landscape === false}
                onChange={() => onLandscapeChange(false)}
                className="w-4 h-4 text-[#40A1AB] focus:ring-[#40A1AB]"
              />
              <span className="font-medium text-sm">Square</span>
            </div>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="h-4 w-4 text-gray-400 hover:text-gray-600 cursor-help" />
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <p className="text-sm">
                    Your image will be centered and cropped to fit a 1:1 square format. Edges may be trimmed.
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>

          <div className="relative w-20 h-20 mx-auto rounded-md overflow-hidden bg-gray-100 mb-2">
            <img
              src={imageUrl || "/placeholder.svg"}
              alt="Square preview"
              className="w-full h-full object-cover object-center"
            />
          </div>

          <div className="text-xs text-center text-gray-600 space-y-0.5">
            <div>1024 × 1024</div>
            <div className="font-medium text-[#40A1AB]">{squareCredits > 0 ? `${squareCredits} credits` : "Free"}</div>
          </div>
        </button>

        {/* Landscape Option */}
        <button
          type="button"
          onClick={() => onLandscapeChange(true)}
          className={`relative p-3 rounded-lg border-2 transition-all ${
            landscape === true ? "border-[#40A1AB] bg-[#40A1AB]/10" : "border-gray-200 hover:border-gray-300"
          }`}
        >
          <div className="flex items-start justify-between mb-2">
            <div className="flex items-center gap-2">
              <input
                type="radio"
                checked={landscape === true}
                onChange={() => onLandscapeChange(true)}
                className="w-4 h-4 text-[#40A1AB] focus:ring-[#40A1AB]"
              />
              <span className="font-medium text-sm">Landscape</span>
            </div>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="h-4 w-4 text-gray-400 hover:text-gray-600 cursor-help" />
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <p className="text-sm">
                    Your image will be centered in a wider 3:2 format. Our AI will intelligently extend the sides based
                    on your room details if necessary.
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>

          <div className="relative w-[120px] h-20 mx-auto rounded-md overflow-hidden bg-gray-200 mb-2">
            <img
              src={imageUrl || "/placeholder.svg"}
              alt="Landscape preview"
              className="w-full h-full object-contain object-center"
            />
          </div>

          <div className="text-xs text-center text-gray-600 space-y-0.5">
            <div>1536 × 1024</div>
            <div className="font-medium text-[#40A1AB]">
              {landscapeCredits > 0 ? `${landscapeCredits} credits` : "Free"}
            </div>
          </div>
        </button>
      </div>
    </div>
  )
}
